import { Component, Inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { WebAppUser } from '@services/authentication.service';
import { Rule, RuleDefinition, RulesDataService } from '@services/rules-data.service';
import { distinctUntilChanged, filter, finalize, firstValueFrom, Subject, take, takeUntil, tap } from 'rxjs';
import { CreateRuleComponent, EditRuleDialogData } from '../create-rule/create-rule.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { DataPoint } from '@services/datapoints-data.service';
import { Router } from '@angular/router';

export class RuleListDialogData {
	asset: LevelAsset;
	isAdmin: boolean;
	currentUser: WebAppUser;
	accountGUID: string;
	organisationUID: string;
}

@Component({
	selector: 'app-rule-list',
	templateUrl: './rule-list.component.html',
	styleUrls: ['./rule-list.component.scss']
})
export class RuleListComponent {

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string

	/** Asset */
	public Asset: LevelAsset;

	/** Rules */
	public Rules: Rule[] = [];

	/** DataPoints */
	public DataPoints: DataPoint[];

	/** isAdmin */
	public isAdmin: boolean = false;

	/** currentUser */
	public currentUser: WebAppUser;

	/** Used when explaining the rules */
	public conditions: { text: string, value: string }[] = [
		{ text: "More Than", value: ">" },
		{ text: "More Than Or Equal To", value: ">=" },
		{ text: "Less Than", value: "<" },
		{ text: "Less Than Or Equal To", value: "<=" },
		{ text: "Equal To", value: "=" },
		{ text: "Not Equal To", value: "<>" },
		{ text: "Between", value: "~" },
		{ text: "Not Between", value: "!~" },
	];

	constructor(
		public dialog: MatDialog,
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<RuleListComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: RuleListDialogData,
		private assetsDataService: AssetsDataService,
		private rulesDataService: RulesDataService,
		private globalFunctions: GlobalFunctions,
		private router: Router
	) {
		this.Asset = this.dialogData.asset;
		this.isAdmin = this.dialogData.isAdmin;
		this.accountGUID = this.dialogData.accountGUID;
		this.organisationUID = this.dialogData.organisationUID;
		this.currentUser = this.dialogData.currentUser;

		this.globalFunctions.displaySpinner(true);
		this.getDatapoints(this.Asset);
		this.getRules(this.Asset);
		this.globalFunctions.displaySpinner(false);
	}

	/** getDatapoints */
	getDatapoints(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getAssetDataPoints$(this.accountGUID, this.organisationUID, asset.AssetID, false).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((asset) => !!asset),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (datapoints: any) => {
				this.DataPoints = datapoints;
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints$", false); },
			//complete: () => { console.log("this.siteSubscriptions.selectedOrganisationUID$.subscribe: Complete"); }
		});
	}

	// /** getRules */
	// getRules(asset: LevelAsset) {
	// 	// Create a local Subject to control the subscription lifecycle
	// 	const fetchDataSubject = new Subject<void>();

	// 	this.rulesDataService.getRules$(this.accountGUID, this.organisationUID, asset.AssetID).pipe(
	// 		takeUntil(fetchDataSubject), distinctUntilChanged(),
	// 		filter((asset) => !!asset),
	// 		finalize(() => {
	// 			// Ensure the subscription is terminated and the subject is completed
	// 			fetchDataSubject.next();
	// 			fetchDataSubject.complete();
	// 		})
	// 	).subscribe({
	// 		next: (rules: any) => {
	// 			if (rules[0].RuleID != 0) {
	// 				this.Rules = rules;
	// 			}
	// 		},
	// 		error: (error) => { this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getRules$", false); },
	// 		//complete: () => { console.log("this.siteSubscriptions.selectedOrganisationUID$.subscribe: Complete"); }
	// 	});
	// }

	/**
	 * getRules
	 * Fetches the rules for a given asset and updates the component's rule list.
	 * @param asset
	 */
	async getRules(asset: LevelAsset) {

		try {
			const AssetRules = await firstValueFrom(
				this.rulesDataService.getRules$(this.accountGUID, this.organisationUID, asset.AssetID).pipe(
					take(1),
					filter((rules) => !!rules)
					, tap(() => this.Rules = [])
				)
			);

			if (AssetRules) {
				if (AssetRules[0].RuleID != 0) {
					this.Rules = AssetRules;
					return;
				}
			}
			this.Rules = [];
		}
		catch (error: any) {
			if (error.status == 404 || error.status == 500) { }
			else { this.globalFunctions.HandleError(error, "Error: rulesDataService.getRules$", false); }
		}
	}

	/**
	 * create a new rule
	 * @param editAsset
	 */
	createRule(editAsset: LevelAsset): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...editAsset };

		let dialogData = <EditRuleDialogData>{
			assetItem: _edit
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			//split the rule into the rule-definition composition
			let data1;
			if (result.condition1 != '~' && result.condition1 != '!~') {
				data1 = {
					Condition: 1,
					DatapointID: result.dataPoint1,
					Limit: result.value1,
					Operator: result.condition1
				}
			} else {
				data1 = {
					Condition: 1,
					DatapointID: result.dataPoint1,
					Limit: result.between1value1,
					LowerLimit: result.between1value2,
					Operator: result.condition1
				}
			}
			let definition1: RuleDefinition = data1;

			let definition2 = undefined;
			if (result.dataPoint2) {
				let data2;
				if (result.condition2 != '~' && result.condition2 != '!~') {
					data2 = {
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
				} else {
					data2 = {
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.between2value1,
						LowerLimit: result.between2value2,
						Operator: result.condition2
					}
				}
				definition2 = data2;
			}

			let data3 = undefined;
			let newRule: Rule;
			if (definition2) {
				data3 = {
					AssetID: editAsset.AssetID,
					RuleDescription: result.ruleDescription,
					RuleText: "[1] " + result.comparison + " [2]",
					RuleMessage: result.ruleMessage,
					RuleEnabled: result.ruleEnabled,
					CMMSEnabled: result.CMMSEnabled,
					TriggerTime: this.timeToMinutes(result.ruleTime),
					Active: false,
					RuleDef: [definition1, definition2]
				}
				newRule = data3;
			} else {
				data3 = {
					AssetID: editAsset.AssetID,
					RuleDescription: result.ruleDescription,
					RuleText: "[1]",
					RuleMessage: result.ruleMessage,
					RuleEnabled: result.ruleEnabled,
					CMMSEnabled: result.CMMSEnabled,
					TriggerTime: this.timeToMinutes(result.ruleTime),
					Active: false,
					RuleDef: [definition1]
				}
				newRule = data3;
			}

			//testing
			// return;

			this.rulesDataService.postRule$(this.accountGUID, this.organisationUID, editAsset.AssetID, newRule, this.currentUser.UserID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.getRules(this.Asset);
				})
			).subscribe({
				next: (response) => {
					// this.GetRules(this.selectedAsset)
					this.globalFunctions.DisplaySnackBar({
						message: "Rule Created",
						type: SnackType.Info,
						duration: 1000
					});
				}
			});
		});
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * Splits the rule info into a readable array of strings
	 * @param rule
	 * @returns
	 */
	explainRule(rule: Rule): string[] {
		let exp: string[] = []
		// exp.push("RULE WILL TRIGGER IF")
		if (rule.RuleDef) {
			for (let i: number = 0; i < rule.RuleDef?.length; i++) {
				let dp: any = this.getDatapointInAssetDataPoints(rule.RuleDef[i].DatapointID)
				if (dp) {
					let condition = this.getTextFromValue(rule.RuleDef[i].Operator);
					exp.push(dp.DPTitle)
					exp.push("Is " + condition)
					exp.push(rule.RuleDef[i].Limit + dp.SI)
					if (rule.RuleDef[i].Operator == '~' || rule.RuleDef[i].Operator == '!~') {
						if (rule.RuleDef[i].LowerLimit != undefined) {
							exp.push("AND");
							exp.push(rule.RuleDef[i].LowerLimit + dp.SI);
						}
					}
					if (i != rule.RuleDef.length - 1) {
						const result = rule.RuleText.match(/AND|OR/)?.[0] || "";
						exp.push(result)
					} else {
						// exp.push("FOR")
						// exp.push(this.convertMinutesToHHMM(rule.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(rule.TriggerTime)[1])
					}
				}
			}
		}
		return exp
	}

	/**
	 * Converts operator symbols into readable strings
	 * @param value
	 * @returns
	 */
	getTextFromValue(value: string): string | undefined {
		const condition = this.conditions.find(cond => cond.value.toLowerCase() === value.toLowerCase());
		return condition ? condition.text : undefined;
	}

	/**
	 * Returns full datapoint object from assets list of datapoints when given
	 * a DataPointID, or undefined if the DataPointID does not match
	 * @param datapointID
	 * @returns
	 */
	getDatapointInAssetDataPoints(datapointID: number): DataPoint | undefined {
		return this.DataPoints.find((x: DataPoint) => x.DataPointID == datapointID)
	}

	/**
	 * OnRuleEnable
	 * @param rule
	 * @param event
	 */
	OnRuleEnable(rule: Rule, event: MatSlideToggleChange) {
		rule.RuleEnabled = event.checked;
		if (event.checked == false) { rule.Active = false; }
		this.patchRule(rule);
	}

	/**
	 * patchRule
	 * @param rule
	 */
	patchRule(rule: Rule, deleted: boolean = false) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.rulesDataService.patchRule$(this.accountGUID,
			this.organisationUID, this.Asset.AssetID, rule, this.currentUser.UserID, deleted).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((sqlResult) => !!sqlResult),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (sqlResult: any) => {
					// console.log(sqlResult)
					this.globalFunctions.DisplaySnackBar({
						message: !deleted ? "Rule Updated" : "Rule Deleted",
						type: SnackType.Info,
						duration: 1000
					});
					if (deleted) {
						this.getRules(this.Asset);
					}
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.rulesDataService.patchRule$", false);
					}
				},
			});
	}

	/**
	 * Delete an existing rule
	 * @param rule
	 * @param event
	 */
	onDeleteRule(rule: Rule, event: MouseEvent) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		event.stopPropagation()
		this.globalFunctions.UserConfirm$("Are you sure you want to delete rule: " + rule.RuleDescription + "?").subscribe(async OKResult => {
			if (OKResult) {
				this.patchRule(rule, true);

				// this.rulesDataService.deleteRule$(this.accountGUID,
				// 	this.organisationUID, rule.RuleID).pipe(
				// 		takeUntil(fetchDataSubject), distinctUntilChanged(),
				// 		filter((sqlResult) => !!sqlResult),
				// 		finalize(() => {
				// 			// Ensure the subscription is terminated and the subject is completed
				// 			fetchDataSubject.next();
				// 			fetchDataSubject.complete();
				// 			this.getRules(this.Asset);
				// 		})
				// 	).subscribe({
				// 		next: (sqlResult: any) => {
				// 			// console.log(sqlResult)
				// 			this.globalFunctions.DisplaySnackBar({
				// 				message: sqlResult['Result'],
				// 				type: (sqlResult['Error'] == '') ? SnackType.Info : SnackType.Error,
				// 				duration: 1000
				// 			});
				// 		},
				// 		error: (error) => {
				// 			if (error.status != 404) {
				// 				this.globalFunctions.HandleError(error, "Error: this.rulesDataService.deleteRule$", false);
				// 			}
				// 		},
				// 	});
			}
		});
	}

	/**
	 * Edit an existing rule
	 * @param rule
	 * @param event
	 */
	onEditRule(rule: Rule, event: MouseEvent) {

		event.stopPropagation()
		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: this.Asset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || ""
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}
			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			//split the rule into the rule-definition composition

			if (rule.RuleDef) {
				let data1;
				if (result.condition1 != '~' && result.condition1 != '!~') {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.value1,
						Operator: result.condition1
					}
				} else {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.between1value1,
						LowerLimit: result.between1value2,
						Operator: result.condition1
					}
				}

				let definition1: RuleDefinition = data1;

				let definition2 = undefined;
				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					let data2;
					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					} else {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					let data2;
					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					} else {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]"
					rule.RuleDef = [definition1]
				}
			}
			this.patchRule(rule)
			// if (this.Asset) { this.updateForm }
			// this.hierarchyChanged.emit(true)
		});
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/** goToRule */
	goToRule(rule: Rule) {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/rules/${rule.RuleID}`]);
		this.dialogRef.close();
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		this.dialogRef.close();
	}

	/**
	 * checkIfBetween
	 * @param rule rule 
	 */
	checkIfBetween(rule: Rule): boolean {
		let result: boolean = false;
		if (rule.RuleDef) {
			if (rule.RuleDef[0].Operator == '~' || rule.RuleDef[0].Operator == '!~') {
				result = true;
			}
			if (rule.RuleDef[1]) {
				if (rule.RuleDef[1].Operator == '~' || rule.RuleDef[1].Operator == '!~') {
					result = true;
				}
			}
		}
		return result;
	}

	/**
	 * 	explainRuleDefintion
	 * @param ruleDefintion 
	 */
	explainRuleDefintion(ruleDefintion: RuleDefinition): string[] {
		let exp: string[] = [];
		let dp: any = this.getDatapointInAssetDataPoints(ruleDefintion.DatapointID)
		if (dp) {
			let condition = this.getTextFromValue(ruleDefintion.Operator);
			exp.push(dp.DPTitle)
			exp.push("Is " + condition)
			exp.push(ruleDefintion.Limit + dp.SI)
			if (ruleDefintion.Operator == '~' || ruleDefintion.Operator == '!~') {
				if (ruleDefintion.LowerLimit != undefined) {
					exp.push("AND");
					exp.push(ruleDefintion.LowerLimit + dp.SI);
				}
			}
		}
		return exp;
	}

	/**
	 * getComparison
	 */
	getComparison(rule: Rule): string {
		return rule.RuleText.match(/AND|OR/)?.[0] || "";
	}
}
